import classnames from 'classnames/bind'
import { useTransition } from 'react'
import { useTranslate } from 'react-polyglot'
import { GlobalThemeColors } from '~/@types/colors'
import { GlobalTextPreset } from '~/@types/text-preset'
import { GlobalTextStyling } from '~/@types/text-styling'

import {
  Accordion,
  AccordionPanel,
} from '@unlikelystudio/react-abstract-components'

import { MinusIcon, PlusIcon } from '~/components/Abstracts/Icons'
import { asText } from '~/components/Abstracts/Prismic/rich-text'
import RichText, { RichTextBlocks } from '~/components/Abstracts/RichText'
import InlineCta from '~/components/UI/InlineCta'

import { useStyle } from '~/providers/StyleProvider'

import { GENERAL } from '~/data/dictionary'

import css from './styles.module.scss'

const cx = classnames.bind(css)

declare global {
  interface Window {
    Optanon: any
  }
}

interface AccordionTitleItem {
  headline?: RichTextBlocks
  text?: RichTextBlocks
  enableCookieTable?: boolean
  enableCookieButton?: boolean
}

export interface AccordionTitleProps {
  className?: string
  titleClassName?: string
  headlineClassName?: string
  textClassName?: string
  accordionClassName?: string
  panelClassName?: string
  title?: RichTextBlocks
  items?: AccordionTitleItem[]
}

function AccordionTitle({
  className,
  title,
  items,
  titleClassName,
  headlineClassName,
  textClassName,
  accordionClassName,
  panelClassName,
}: AccordionTitleProps) {
  const titleStyle = useStyle({
    textPreset: GlobalTextPreset.Title12_18Haffer,
    color: GlobalThemeColors.Black,
    textStyling: GlobalTextStyling.UpperCase,
  })
  const headlineStyle = useStyle({
    textPreset: GlobalTextPreset.Title12Haffer,
    color: GlobalThemeColors.Black,
    textStyling: GlobalTextStyling.UpperCase,
  })
  const textStyle = useStyle({
    textPreset: GlobalTextPreset.Text12Haffer,
    color: GlobalThemeColors.MineShaft,
  })

  const t = useTranslate()
  const [isPending, startTransition] = useTransition()

  return (
    <div className={cx(css.AccordionTitle, className)}>
      <RichText
        className={cx(css.title, titleStyle, titleClassName)}
        render={title}
      />

      {items?.length > 0 && (
        <Accordion className={cx(css.accordion, accordionClassName)} index={0}>
          {items?.map(
            (
              { headline, text, enableCookieTable, enableCookieButton },
              index,
            ) => (
              <AccordionPanel
                ariaLabels={{
                  open: `${t(GENERAL.ACCORDION_OPEN)} 
                  ${asText(headline)}`,
                  close: `${t(GENERAL.ACCORDION_CLOSE)} 
                  ${asText(headline)}`,
                }}
                key={`panel_${index}`}
                index={index}
                className={cx(css.panel, panelClassName)}
                head={(isActive: boolean) => {
                  return (
                    <div
                      className={cx(css.panelHead, {
                        isActive,
                      })}>
                      <RichText
                        className={cx(
                          css.headline,
                          headlineStyle,
                          headlineClassName,
                        )}
                        ctaPreset={GlobalTextPreset.Cta12Haffer}
                        uppercaseLink={false}
                        render={headline}
                      />

                      {isActive ? (
                        <MinusIcon className={css.icon} fill="#000000" />
                      ) : (
                        <PlusIcon className={css.icon} fill="#000000" />
                      )}
                    </div>
                  )
                }}
                body={
                  <div className={cx(css.panelText)}>
                    <RichText
                      className={cx(css.text, textStyle, textClassName)}
                      ctaPreset={GlobalTextPreset.Cta12Haffer}
                      uppercaseLink={false}
                      render={text}
                    />
                    {enableCookieTable && (
                      <div
                        className={css.cookiesList}
                        id="ot-sdk-cookie-policy"
                      />
                    )}
                    {enableCookieButton && (
                      <InlineCta
                        className={css.inlineLink}
                        textPreset={GlobalTextPreset.Cta12Haffer}
                        theme={GlobalThemeColors.Black}
                        isLoading={isPending}
                        onClick={() => {
                          startTransition(() => {
                            window?.Optanon?.ToggleInfoDisplay?.()
                          })
                        }}>
                        {t(GENERAL.COOKIE_EDIT_CONSENT)}
                      </InlineCta>
                    )}
                  </div>
                }
              />
            ),
          )}
        </Accordion>
      )}
    </div>
  )
}

AccordionTitle.defaultProps = {}

export default AccordionTitle
